import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { API_TAGS } from '@/shared/api';
import { createUser, fetchUserById, fetchUsers, setOwnPassword, updateUser, updateUserPassword } from './api';

type UsersListParams = {
  isActive?: boolean | null;
  name?: string | null;
  email?: string | null;
  limit?: number;
  offset?: number;
};

/**
 * Query keys for users
 */
export const usersKeys = {
  all: [API_TAGS.USERS] as const,
  lists: () => [...usersKeys.all, 'list'] as const,
  list: (filters: UsersListParams = {}) => [...usersKeys.lists(), { ...filters }] as const,
  details: () => [...usersKeys.all, 'detail'] as const,
  detail: (id: number) => [...usersKeys.details(), id] as const,
};

/**
 * Hook to fetch all users
 */
export const useUsers = (params?: UsersListParams) => {
  return useQuery({
    queryKey: usersKeys.list(params),
    queryFn: () => fetchUsers(params),
  });
};

/**
 * Hook to fetch users with infinite scroll
 */
export const useInfiniteUsers = (params?: UsersListParams) => {
  const limit = params?.limit || 50;
  const initialPageParam = Math.floor((params?.offset || 0) / limit);

  const query = useInfiniteQuery({
    queryKey: usersKeys.list(params),
    queryFn: ({ pageParam }) => fetchUsers({ ...params, limit, offset: pageParam * limit }),
    initialPageParam,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      return lastPage.total <= limit * (lastPageParam + 1) ? undefined : lastPageParam + 1;
    },
  });

  const data = useMemo(
    () => ({ total: query.data?.pages[0]?.total, users: query.data?.pages.flatMap((p) => p.users) }),
    [query.data],
  );

  return {
    ...query,
    data,
  };
};

/**
 * Hook to fetch a user by ID
 * @param id - User ID
 */
export const useUserById = (id: number) => {
  return useQuery({
    queryKey: usersKeys.detail(id),
    queryFn: () => fetchUserById(id),
    enabled: typeof id === 'number' && !Number.isNaN(id),
  });
};

/**
 * Hook to update a user by ID
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUser,
    onSuccess: (_data, variables) => {
      // Invalidate the users list query to refetch the data
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      // Invalidate the specific user detail query
      queryClient.invalidateQueries({ queryKey: usersKeys.detail(variables.id) });
    },
  });
};

/**
 * Hook to create a user
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createUser,
    onSuccess: (data) => {
      // Invalidate the users list query to refetch the data
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      // Set the new user in cache
      queryClient.setQueryData(usersKeys.detail(data.id), data);
    },
  });
};

/**
 * Hook to create a user
 */
export const useUpdateUserPassword = () => {
  return useMutation({
    mutationFn: updateUserPassword,
  });
};

/**
 * Хук для установки постоянного пароля текущим пользователем (PUT /user/me/password)
 */
export const useSetOwnPassword = () => {
  return useMutation({
    mutationFn: setOwnPassword,
  });
};
